import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  SafeAreaView,
  Image,
  Alert,
  Modal,
  TextInput
} from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from '../../../../hooks/useApiWithLoading';
import { buildBillPayValidationPayload, callValidateBillPay } from "../../../../helper/billPayValidationHelper";
import { buildBillPayFeeLookupPayload, callPostBillPayFeeLookup, buildBillPayGffpPayload, callGetBillPayFieldsForProduct } from "../../../../helper/billPayFeelookupHelper";
import { ProductType } from "../../../../view-models/productType";
// import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { ComponentStyles } from "../../../../constants/componentStyles";

// Icons
import BackIcon from "../../../../../../../assets/images/back_icon.png";
import HelpIcon from "../../../../../../../assets/images/Icon_help.png";
import CloseIcon from "../../../../../../../assets/images/Icon_close.png";
import { updateConsumerProfile, postTransactionToKiosk, postAuthConsumer } from "../../../../services/send.service";
import { validateConsumer } from "../../../../services/receive.service";
import { getFieldsForProductRequest } from "../../../../services/receive.service";
import { buildSendValidationRequestFromStore } from "../../../../helper/sendValidationHelper";
import { buildTransactionParametersFromStore } from "../../../../helper/transactionParameterHelper";

// Bill Pay flow step components
import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep";
import EnterPinStep from "./steps/EnterPinStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import ChooseCompanyStep from "./steps/ChooseCompanyStep";
import AccountInformationStep from "./steps/AccountInformationStep";
import PayAmountStep from "./steps/PayAmountStep";
import BillPayReviewAndEditStep from "./steps/BillPayReviewAndEditStep";
import BillPayDOBStep from "./steps/BillPayDOBStep";
import FraudPreventionStep from "../../common/steps/FraudPreventionStep";
import BillPayFinalReviewStep from "./steps/BillPayFinalReviewStep";
import BillPayHeadToCashierStep from "./steps/BillPayHeadToCashierStep";

// New Consumer step components
import EnterZipCodeStep from "./steps/EnterZipCodeStep";
import BillPayAddressStep from "./steps/BillPayAddressStep";
import BillPayEnterNameStep from "./steps/BillPayEnterNameStep";
import BillPayConfirmPinStep from "./steps/BillPayConfirmPinStep";

import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { logUpdateConsumerProfileDraft } from "../../../../helper/createProfileHelper";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../../../../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { buildAuthConsumerPostParameter } from "../../../../helper/authConsumerHelper";
import { AuthConsumerInfo } from "../../../../view-models/AuthConsumerInfo";
import { postReferenceNumberSearch } from "../../../../services/receive.service";
import { TransactionStatus } from "../../../../view-models/TransactionStatus";
import { ReferenceNumberInfo } from "../../../../view-models/ReferenceNumberInfo";
import { buildReceiveValidationRequestFromStore } from "../../../../helper/receiveValidationHelper";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import PreviosBillerInfoStep from "./steps/PreviosBillerInfoStep";
import { FeeLookupInfo } from "../../../../view-models/feeInfo";
import AdditionalInformationStep from "./steps/AdditionalInformationStep";
import { ConsumerRegistration } from "../../../../view-models/ConsumerRegistration";
import { ItemChoiceType } from "../../../../view-models/itemChoiceType";
import  Colors  from "../../../../constants/colors";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
}

const EXISTING_CUNSUMER_STEPS = [
  { key: "phone", title: "Enter phone number", component: EnterPhoneStep },
  { key: "pin", title: "Enter your PIN", component: EnterPinStep },
  { key: "previosBillerInfo", title: "Your Address", component: PreviosBillerInfoStep },
  { key: "accountInformation", title: "Choose State of ID", component: AccountInformationStep },
  { key: "payAmount", title: "Pay Amount", component: PayAmountStep },
  { key: "reviewAndEdit", title: "Review", component: BillPayReviewAndEditStep },
  { key: "billPayDOB", title: "Your Date of Birth", component: BillPayDOBStep },
  { key: "additionalInformation", title: "Additional Information", component: AdditionalInformationStep },
  { key: "fraudPrevention", title: "Fraude Prevention", component: FraudPreventionStep },
  { key: "billPayFinalReview", title: "Review", component: BillPayFinalReviewStep },
  { key: "billPayHeadToCashier", title: "Your identification number", component: BillPayHeadToCashierStep }
];

const NEW_CONSUMER_STEPS = [
  { key: "phone", title: "Enter phone number", component: EnterPhoneStep },
  { key: "previosBillerInfo", title: "Your Address", component: PreviosBillerInfoStep },
  { key: "accountInformation", title: "Choose State of ID", component: AccountInformationStep },
  { key: "payAmount", title: "Pay Amount", component: PayAmountStep },
  { key: "reviewAndEdit", title: "Review", component: BillPayReviewAndEditStep },
  { key: "billPayDOB", title: "Your Date of Birth", component: BillPayDOBStep },
  // { key: "billPayZipCode", title: "Your zip code", component: EnterZipCodeStep },
  { key: "billPayAddress", title: "Your Address", component: BillPayAddressStep },
  { key: "billPayFullName", title: "Your Name", component: BillPayEnterNameStep },
  { key: "fraudPrevention", title: "Fraude Prevention", component: FraudPreventionStep },
  { key: "billPayFinalReview", title: "Review", component: BillPayFinalReviewStep },
  { key: "pin", title: "Enter your PIN", component: EnterPinStep },
  { key: 'confirmPin', title: "Confirm you PIN", component: BillPayConfirmPinStep},
  { key: 'pinCreated', title: "PIN Created", component: ConfirmationStep},
  { key: "billPayHeadToCashier", title: "Your identification number", component: BillPayHeadToCashierStep }
]


export const MainBillPayScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish
}) => {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [steps, setSteps] = useState(EXISTING_CUNSUMER_STEPS); // temporary
  const [currentStep, setCurrentStep] = useState(0);
  const [returnToStep, setReturnToStep] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const phoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);
  const isReceiver = useSessionStore((s: SessionState) => (s as any).isReceiver);
  const isSendCash = useSessionStore((s: SessionState) => (s as any).isSendCash);
  console.log('isSendCash==>', isSendCash, "isReceiver==>", isReceiver)
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOptionValue = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const billPayBillerSearchResponseStore = useSessionStore((s: SessionState) => (s as any).billPayBillerSearchResponseStore);
  const billPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);
  const setBillPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayFeeLookupResponseStore);
  const setBillPayGFFPResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayGFFPResponseStore);
  const billPayFeeLookupResponseStore = useSessionStore((s: SessionState) => (s as any).billPayFeeLookupResponseStore);
  const setBillPayAllRequiredFieldsStore = useSessionStore((s: SessionState) => (s as any).setBillPayAllRequiredFieldsStore);
  const billPayAllRequiredFieldsStore = useSessionStore((s: SessionState) => (s as any).billPayAllRequiredFieldsStore);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const setLastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).setLastFeeLookupResponse);
  const setGffpCategories = useSessionStore((s: SessionState) => (s as any).setGffpCategories);
  const setGffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).setGffpFieldsByCategory);
  const setMissingRequiredFields = useSessionStore((s: SessionState) => (s as any).setMissingRequiredFields);
  const selectedIdPhotoIdState = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdState);
  const selectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdCountry);
  const recordStepTransition = useSessionStore((s: SessionState) => (s as any).recordStepTransition);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  // Sender profile setters to hydrate from AuthConsumer
  const setConsumerId = useSessionStore((s: SessionState) => (s as any).setConsumerId);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);
  const setPhoneNumber = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setConfirmationNumber = useSessionStore((s: SessionState) => (s as any).setConfirmationNumber);
  const setReferenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).setReferenceNumberSearchResult);
  const setBillPayGetAuthConsumerResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayGetAuthConsumerResponseStore);
  const setBillPayValidationResponseStore = useSessionStore((s: SessionState) => (s as any).setBillPayValidationResponseStore);

  const consumerIdFromStore = useSessionStore((s: SessionState) => (s as any).consumerId);
  const billPayGetAuthConsumerResponseStore = useSessionStore((s: SessionState) => (s as any).billPayGetAuthConsumerResponseStore);
  const senderFirstNameFromStore = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderLastNameFromStore = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderAddressFromStore = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCityFromStore = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderStateFromStore = useSessionStore((s: SessionState) => (s as any).senderState);
  const senderZipCodeFromStore = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderDOBStringFromStore = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const senderDOBFromStore = useSessionStore((s: SessionState) => (s as any).senderDOB);
  const phoneNumberFromStore = useSessionStore((s: SessionState) => (s as any).phoneNumber);
  const consumerPin = useSessionStore((s: SessionState) => (s as any).consumerPin);
  const setMgiTransactionSessionId = useSessionStore((s: SessionState) => (s as any).setMgiTransactionSessionId);
  const StepComponent = steps[currentStep].component;
  const AnyStepComponent = StepComponent as any;
  const [rrnFieldLabel, setRrnFieldLabel] = useState<string | undefined>(undefined);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [showRefResultModal, setShowRefResultModal] = useState(false);
  const [refResult, setRefResult] = useState<{ receiverName?: string; senderName?: string; receiveAmount?: string | number; referenceNumber?: string | number } | null>(null);
  const receiverReceiveAmount = useSessionStore((s: SessionState) => (s as any).receiverReceiveAmount);
  const setReceiveAmountValidationError = useSessionStore((s: SessionState) => (s as any).setReceiveAmountValidationError);
  const referenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).referenceNumberSearchResult);

  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [resetSignal, setResetSignal] = useState(0);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);
  const inputs = useRef<(TextInput | null)[]>([]);
  const [locked, setLocked] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);


  // initialize timing for first step
  useEffect(() => {
    try { recordStepTransition?.(steps[0].key); } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleEditStep = (key: string) => {
    console.log('handleEditStep=>', key)
    const targetIndex = steps.findIndex(s => s.key === key);
    if (targetIndex !== -1) {
      console.log('setReturnToStep=>', steps[currentStep].key, 'targetIndex==>', targetIndex)
      setReturnToStep(steps[currentStep].key);   // "reviewAndEditStep"✅ remember where we came from
      setCurrentStep(targetIndex); // 7th index
    }
  };

  // const handleForgotPassword = () => {
  //   try { setIsNewConsumer?.(true); } catch {}
  //   setLocked(false);
  //   const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
  //   if (targetIndex !== -1) {
  //     setCurrentStep(targetIndex);
  //   }
  // };
  const handleForgotPassword = () => {
    console.log('handleForgotPassword currentStep==>', currentStep)
    // mark as new consumer so finalReview routes to PIN
    setIsNewConsumer?.(true);
    // Close pop up
    setShowPopup(false);
    setLocked(false);

    //find index from the NEW flow, not the current one
    const newFlow = NEW_CONSUMER_STEPS;
    const countryIndex = newFlow.findIndex(s => s.key === 'previosBillerInfo');

    // update both pieces of state together
    setSteps(newFlow);
    if (countryIndex !== -1) {
      setCurrentStep(countryIndex);
    }
  }

  const handleNext = async (countryCode?: string) => {
    console.log('handleNext===>', handleNext);
    await executeWithLoading(async () => {
      // Return to previous step while edit flow
      if (returnToStep) {
        console.log('returnToStep===>', returnToStep);
        const targetIndex = steps.findIndex(s => s.key === returnToStep);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
        }
        setReturnToStep(null);   // clear for next time
        return;                  // stop normal forward flow
      }
      if (steps[currentStep].key === "phone") {
        const result = await phoneStepRef.current?.submit();
         if (result === 'notfound') {
            // Treat as new consumer
            handleCustomerNotFound();
            setSteps(() => {
              return NEW_CONSUMER_STEPS;
            });
            // advance on the next render so BASE_STEPS_NEW is fixed
            requestAnimationFrame(() => setCurrentStep(
                NEW_CONSUMER_STEPS.findIndex(s => s.key === 'previosBillerInfo')
            ));
            return;
          } else {
            // Existing consumer
            setSteps(EXISTING_CUNSUMER_STEPS);
            setCurrentStep(1); // move to PinInput
          }
        console.log('CHECK INDEx key===', steps[currentStep].key)
        const targetIndex = steps.findIndex(s => s.key === "pin");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "pin") {
         // If this is a new consumer, go to confirm PIN step instead of auth
        if (isNewConsumer) {
          const confirmIndex = steps.findIndex(s => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try { recordStepTransition?.("confirmPin"); } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        } 
           await handlePinVerification();
          //TODO: we need to load previosBillerInfo only when billerInfo found in getAuthConsumerResponse
          // const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
          // if (targetIndex !== -1) {
          //   setCurrentStep(targetIndex);
          //   return;
          // }
      }  else if (steps[currentStep].key === "confirmPin") {
        // After confirming PIN for a new consumer, go to PIN created or head to cashier path
        // No early redirect to additionalInformation here; we will handle missing fields after receiverName
        const result = await handleBillPayFinalReview();
        if (result && (result as any).error) {
          // An error was shown; stay on current step
          return;
        }
        if(isNewConsumer) {
          const targetIndex = steps.findIndex(s => s.key === "pinCreated");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("pinCreated"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } 
      else if (steps[currentStep].key === "previosBillerInfo") {
        const targetIndex = steps.findIndex(s => s.key === "accountInformation");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "accountInformation") {
        const targetIndex = steps.findIndex(s => s.key === "payAmount");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "payAmount") {
        const ok = await handlePayAmount();
        if (!ok) {
          return; // stay on this step when fee lookup/gffp fails
        }
        const targetIndex = steps.findIndex(s => s.key === "reviewAndEdit");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "reviewAndEdit") {
        const targetIndex = steps.findIndex(s => s.key === "billPayDOB");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayDOB") {
        // After DOB, ensure all required fields are present; if missing, branch to AdditionalInformation
        if(!isNewConsumer){
          try {
          const missing = computeBillPayMissingRequiredFields();
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch { }
        }
        // Check consumer is new or existing and then move
        const targetKey = isNewConsumer ? 'billPayAddress' : 'fraudPrevention';
        const targetIndex = steps.findIndex(s => s.key === targetKey);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayAddress") {
        const targetIndex = steps.findIndex(s => s.key === "billPayFullName");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayFullName") {
         // After receiver name, check for any missing required fields from GFFP
         try {
          const missing = computeBillPayMissingRequiredFields();
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            // Route to Additional Information, then return back to Fraud Prevention after completion
            setReturnToStep("fraudPrevention");
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch (e){
          console.warn('Failed to compute missing fields after receiverName', e);
        }
        // No missing fields → proceed to Fraud Prevention
        const targetIndex = steps.findIndex(s => s.key === "fraudPrevention");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "additionalInformation") {
        const nextIdx = steps.findIndex(s => s.key === "fraudPrevention");
        if (nextIdx !== -1) {
          try { recordStepTransition?.("fraudPrevention"); } catch { }
          setCurrentStep(nextIdx);
          return;
        }
      } else if (steps[currentStep].key === "fraudPrevention") {
        const targetIndex = steps.findIndex(s => s.key === "billPayFinalReview");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayFinalReview"); } catch { }
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayFinalReview") {
        // const ok = await handleBillPayFinalReview();
        // if (!ok) {
        //   return; // stay on this step when validation fails
        // }
        // const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        // if (targetIndex !== -1) {
        //   try { recordStepTransition?.("billPayHeadToCashier"); } catch { }
        //   setCurrentStep(targetIndex);
        //   return;
        // }
                // If this is a new consumer, send them to the PIN step before proceeding
        if (isNewConsumer) {
          const pinIndex = steps.findIndex(s => s.key === "pin");
          if (pinIndex !== -1) {
            try { recordStepTransition?.("pin"); } catch { }
            setCurrentStep(pinIndex);
            return;
          }
        } else {
          // For existing consumers, perform update/validate/post flow here.
          // If validation reports missing fields, redirect to Additional Information.
          try {
            const result = await handleBillPayFinalReview();
            if (result && (result as any).error) {
              // An error was shown; stay on current step
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            console.warn('maultipleCalls failed', e);
          }
        }
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "pinCreated") {
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("billPayHeadToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "billPayHeadToCashier") {
        try { handleFinish?.(); } catch {}
        // Build and log update consumer profile draft before proceeding
        const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
      console.log("✅ Flow completed");
    }, 'Processing...');
  };

  const handlePinVerification = async () => {
    // replace with real validation
    try {
      const authConsumerPostParameter = buildAuthConsumerPostParameter();
      console.log("authConsumerPostParameter===", authConsumerPostParameter);
      const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(authConsumerPostParameter);
      console.log("authConsumerResponse===", authConsumerResponse);
      const respAny: any = authConsumerResponse as any;
      if (respAny?.ErrorDetail || respAny?.errorDetail) {
        console.log('Wrong PIN==>', attemptsLeft)
        const remaining = attemptsLeft - 1;
        setAttemptsLeft(remaining);
        setResetSignal(prev => prev + 1);
        setConsumerPin("");
        inputs.current[0]?.focus();
        if (remaining <= 0) {
          setLocked(true);
          setShowPopup(true);
        }
        // try { showError('Incorrect PIN'); } catch {}
        return; // stay on PIN step when auth returns an error detail
      }
      // Persist full response for reuse across steps
      try { setBillPayGetAuthConsumerResponseStore?.(respAny); } catch { }
      // Hydrate sender profile details into the store from successful AuthConsumer
      const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
      if (ci) {
        // Consumer ID
        try { setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? '')); } catch { }
        try { setSenderFirstName?.(ci.SenderFirstName ?? ci.senderFirstName ?? null); } catch { }
        try { setSenderMiddleName?.(ci.SenderMiddleName ?? ci.senderMiddleName ?? null); } catch { }
        try { setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null); } catch { }
        try { setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null); } catch { }
        try { setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null); } catch { }
        try { setSenderState?.(ci.SenderState ?? ci.senderState ?? null); } catch { }
        try { setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null); } catch { }
        // Phone
        try { setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null); } catch { }
        // DOB
        const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
        if (dobRaw) {
          const dobStr = String(dobRaw).split('T')[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
          try { setSenderDOBString?.(dobStr || null); } catch { }
          const dobDate = new Date(dobStr);
          if (!isNaN(dobDate as any)) {
            try { setSenderDOB?.(dobDate); } catch { }
          }
        }
      }
    } catch { }
    const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
      return;
    }
  };

  const handlePayAmount = async (): Promise<boolean> => {
    try {
      const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
      //CHECK: IAM ASSUMING THERE IS ONLY ONE BILLER INFO, SO USING 0 INDEX
      const receiveCode = billerInfo?.BillerInfos[0].ReceiveCode;
      const receiveAgentID = billerInfo?.BillerInfos[0].ReceiveAgentId;
      const amountNum = Number(String(currentSendAmount).replace(/,/g, ""));
      const payload = buildBillPayFeeLookupPayload({
        agentID: agentId,
        unitProfileID: unitProfileId,
        AmountItem: amountNum,
        amountExcludingFee: amountNum,
        receiveCode,
        receiveAgentID,
      });
      let feeResp: FeeLookupInfo;
      try {
        feeResp = await callPostBillPayFeeLookup(payload);
        const rawData: any = (feeResp && (feeResp as any).data !== undefined)
        ? (feeResp as any).data
        : feeResp;
        try { setBillPayFeeLookupResponseStore?.(rawData); } catch { }
        handleError(rawData);
      } catch (e: any) {
        return false; // stay on this step
      }
     
      // On successful fee lookup, fetch fields for product
      try {
        const gffpPayload = buildBillPayGffpPayload({
          agentID: agentId,
          unitProfileID: unitProfileId,
          receiveAgentID: receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          amount: currentSendAmount,
          customerId: consumerIdFromStore || "0",
        });
        try {
          const gffpResp = await callGetBillPayFieldsForProduct(gffpPayload);
          const billPayAllRequiredFields: any[] = GffpHelper.filterRequiredAndOptionalFields(gffpResp) as any[];

          try { setBillPayGFFPResponseStore?.(gffpResp); } catch { }
          try { setBillPayAllRequiredFieldsStore?.(billPayAllRequiredFields); } catch { }
        } catch (e: any) {
          return false; // stay on this step
        }
      } catch (e) {
        // Defensive catch; error already handled above
      }
    } catch (e) {
      return false; // stay on this step
    }
    return true;
  };

  const handleBillPayFinalReview = async (options?: { skipUpdateProfile?: boolean }): Promise<boolean> => {
    const skipUpdate = !!options?.skipUpdateProfile;
     // Ensure no required fields are missing before calling API; if missing, route to AdditionalInformation
     try {
      try {
        const missing = computeBillPayMissingRequiredFields();
        if (missing && missing.length > 0) {
          try { setMissingRequiredFields?.(missing as any); } catch {}
          const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
          if (aiIndex !== -1) {
            setCurrentStep(aiIndex);
          }
          return false;
        }
      } catch {}
      // handle skip for new consumer in pin screen
      let effectiveConsumerId: string | undefined = undefined;
      if (!skipUpdate) {
        const profile: SendMoneyPostUpdateConsumerProfileParameter = logUpdateConsumerProfileDraft();
        profile.unitProfileID = unitProfileId || "";
        profile.agentID = agentId || "";
        console.log("consumer profile===", profile);
        const profileResponse = await updateConsumerProfile(profile);
        console.log("profileResponse===", profileResponse);
        try {
          effectiveConsumerId = (profileResponse && (profileResponse.consumerId || profileResponse.ConsumerId || profileResponse.id || profileResponse.Id)) ?? undefined;
        } catch {}
      } else {
        // For skip flow, attempt to use existing consumerId from store; fall back to "0"
        try { effectiveConsumerId = consumerIdFromStore ? String(consumerIdFromStore) : "0"; } catch { effectiveConsumerId = "0"; }
      }
      // Build ValidateBillPay payload and call API
      const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
      const receiveCode = billerInfo?.BillerInfos?.[0]?.ReceiveCode;
      const receiveAgentID = billerInfo?.BillerInfos?.[0]?.ReceiveAgentId;

      const amountNumber = Number(String(currentSendAmount || "0").replace(/,/g, ""));

      // Normalize inputs that may be JSON strings
      const normalizedFeeLookup: any = (() => {
        let raw: any = billPayFeeLookupResponseStore as any;
        if (typeof raw === 'string') {
          try { raw = JSON.parse(raw); } catch {}
        }
        return raw;
      })();
     

      const sessionId = (
         normalizedFeeLookup?.FeeInfos?.[0]?.MgiTransactionSessionId
      ) as string | undefined;

      const profilePreferenceId = billPayGetAuthConsumerResponseStore?.ConsumerInfo?.BillerLookupInfos[0].ProfilePreferenceId;

      const formatDob = (): string | undefined => {
        if (senderDOBStringFromStore) return senderDOBStringFromStore as string;
        const d = senderDOBFromStore as Date | null;
        if (d && !isNaN(d as any)) {
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const yyyy = d.getFullYear();
          return `${mm}/${dd}/${yyyy}`;
        }
        return undefined;
      };

      let payload: any = {}
      if (isNewConsumer) {
        const reg = new ConsumerRegistration();
        reg.consumerLoginID = phoneNumberFromStore
        // reg.consumerPIN = consumerPin ? consumerPin : '7956';//TODO: What if user skip the pin, Handle it
        reg.item = consumerPin ? consumerPin : '7956'
        reg.itemElementName = 'ConsumerPin';
          
        payload = {
          unitProfileID: unitProfileId,
          agentID: agentId,
          productVariant: 'EP',
          amount: amountNumber,
          destinationCountry: 'USA',
          receiveCode,
          receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          senderFirstName: senderFirstNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName,
          senderLastName: senderLastNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
          senderAddress: senderAddressFromStore,
          senderCity: senderCityFromStore,
          senderState: senderStateFromStore,
          senderZipCode: senderZipCodeFromStore,
          senderCountry: 'USA',
          senderHomePhone: phoneNumberFromStore,
          senderDOB: formatDob(),
          accountNumberRetryCount: 1,
          sendCurrency: 'USD',
          receiveCurrency: 'USD',
          cardSwiped: false,
          mgiTransactionSessionID: sessionId,
          formFreeStaging: true,
          consumerId: effectiveConsumerId,
          consumerProfileUpdate: true,
          // profilePreferenceId: profilePreferenceId,
          panAvailable: false,
          consumerRegistration: reg
        };
      } else {
        payload = {
          unitProfileID: unitProfileId,
          agentID: agentId,
          productVariant: 'EP',
          amount: amountNumber,
          destinationCountry: 'USA',
          receiveCode,
          receiveAgentID,
          billerAccountNumber: billPayBillerAccountNumberStore,
          senderFirstName: senderFirstNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName,
          senderLastName: senderLastNameFromStore || billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
          senderAddress: senderAddressFromStore,
          senderCity: senderCityFromStore,
          senderState: senderStateFromStore,
          senderZipCode: senderZipCodeFromStore,
          senderCountry: 'USA',
          senderHomePhone: phoneNumberFromStore,
          senderDOB: formatDob(),
          accountNumberRetryCount: 1,
          sendCurrency: 'USD',
          receiveCurrency: 'USD',
          cardSwiped: false,
          mgiTransactionSessionID: sessionId,
          formFreeStaging: true,
          consumerId: consumerIdFromStore ? String(consumerIdFromStore) : undefined,
          consumerProfileUpdate: true,
          profilePreferenceId: profilePreferenceId,
          panAvailable: false,
        };
      }

      try {
        const validationPayload = buildBillPayValidationPayload(payload as any);
        const validationResp = await callValidateBillPay(validationPayload);
        try { setBillPayValidationResponseStore?.(validationResp); } catch {}

        const noError = handleError(validationResp);
        if (noError === false) {
          return false; // stop progression on validation error
        }

        try {
          const respAny: any = validationResp || {};
          const conf = respAny?.confirmationNumber || respAny?.ConfirmationNumber;
          if (conf) { try { setConfirmationNumber?.(String(conf)); } catch { } }
          const respSessionId = respAny?.mgiTransactionSessionId || respAny?.MgiTransactionSessionId || respAny?.mgiTransactionSessionID || respAny?.MgiTransactionSessionID;
          if (respSessionId) { try { setMgiTransactionSessionId?.(String(respSessionId)); } catch { } }

          // If confirmation number exists, construct and post transaction parameters
          if (conf) {
            try {
              const billerName = selectedBillerInformation?.BillerName;
              
              const txParams = buildTransactionParametersFromStore(validationResp, 'billPay', respSessionId,
                billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderFirstName + ' ' + billPayGetAuthConsumerResponseStore?.ConsumerInfo?.SenderLastName,
                billerName
               );
              console.log('transactionParameters===', txParams);
              const txResp = await postTransactionToKiosk(txParams);
              console.log('postTransactionToKiosk response===', txResp);
            } catch (e) {
              console.warn('Failed to post transaction to kiosk', e);
            }
          }
        } catch { }
      } catch (e: any) {
        return false; // stay on this step
      }
    } catch (e) {
      return false; // stay on this step
    }
    return true;
  };

  const handleError = (resp: any) => {
    try {
      let raw: any = (resp && (resp as any).data !== undefined)
        ? (resp as any).data
        : resp;
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch {}
      }
      const errDetailRaw: any = raw?.ErrorDetail ?? raw?.errorDetail ?? null;
      const errDetail: any = Array.isArray(errDetailRaw) ? errDetailRaw[0] : errDetailRaw;
      if (errDetail) {
        const code = errDetail?.ErrorCode ?? errDetail?.errorCode ?? '';
        const errStr = errDetail?.ErrorString ?? errDetail?.errorString ?? '';
        const field = errDetail?.OffendingField ?? errDetail?.offendingField ?? '';
        const message = `There was a problem with your transaction. ${[code, errStr, field].filter(Boolean).join(' ')}`;
        try { showError(message); } catch {}
        return false; // stop further processing on validation error
      }
      return true; // no error
    } catch (e) {
      return false; // stay on this step
    }
  }

  const handleSkip = () => {
    // Run validation + transaction only, then jump to HeadToCashier
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeWithLoading(async () => {
     try {
        const result = await handleBillPayFinalReview({ skipUpdateProfile: true });
        if (result && (result as any).error) {
          // An error was shown; stay on current step
          return;
        }
      } catch (e) {
        console.warn('Skip flow failed', e);
      }
      const targetIndex = steps.findIndex(s => s.key === "billPayHeadToCashier");
      if (targetIndex !== -1) {
        try { recordStepTransition?.("billPayHeadToCashier"); } catch { }
        setCurrentStep(targetIndex);
      }
    }, 'Processing...');
  };

  // Enable Continue button for SendingAmountEntry step
  useEffect(() => {
    if (steps[currentStep].key === "sendingAmountEntry") {
      setIsStepValid(true);
    } else if (steps[currentStep].key !== "phone") {
      // Default allow for other non-phone steps unless they manage validation
      setIsStepValid(true);
    } else {
      // Phone step manages its own
      setIsStepValid(false);
    }
  }, [currentStep]);

  // Build a Bill Pay validation draft including AdditionalInformation values
  const buildBillPayValidationDraft = (): any => {
    const normalizedFeeLookup: any = (() => {
      let raw: any = billPayFeeLookupResponseStore as any;
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch {}
      }
      return raw;
    })();
    const sessionId = (
      normalizedFeeLookup?.FeeInfos?.[0]?.MgiTransactionSessionId
    ) as string | undefined;
    const billerInfo: any = (billPayBillerSearchResponseStore?.BillerInfo ?? billPayBillerSearchResponseStore?.billerInfo ?? billPayBillerSearchResponseStore) as any;
    const receiveCode = billerInfo?.BillerInfos?.[0]?.ReceiveCode;
    const receiveAgentID = billerInfo?.BillerInfos?.[0]?.ReceiveAgentId;
    const amountNumber = Number(String(currentSendAmount || '0').replace(/,/g, '')) || 0;
    const payloadDraft = buildBillPayValidationPayload({
      unitProfileID: unitProfileId,
      agentID: agentId,
      productVariant: 'EP',
      amount: amountNumber,
      destinationCountry: 'USA',
      receiveCode,
      receiveAgentID,
      billerAccountNumber: billPayBillerAccountNumberStore,
      senderFirstName: senderFirstNameFromStore,
      senderLastName: senderLastNameFromStore,
      senderAddress: senderAddressFromStore,
      senderCity: senderCityFromStore,
      senderState: senderStateFromStore,
      senderZipCode: senderZipCodeFromStore,
      senderCountry: 'USA',
      senderHomePhone: phoneNumberFromStore,
      senderDOB: ((): string | undefined => {
        if (senderDOBStringFromStore) return senderDOBStringFromStore as string;
        const d = senderDOBFromStore as Date | null;
        if (d && !isNaN(d as any)) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const yyyy = d.getFullYear();
          return `${mm}/${dd}/${yyyy}`;
        }
        return undefined;
      })(),
      accountNumberRetryCount: 1,
      sendCurrency: 'USD',
      receiveCurrency: 'USD',
      cardSwiped: false,
      mgiTransactionSessionID: sessionId,
      formFreeStaging: true,
      consumerId: consumerIdFromStore ? String(consumerIdFromStore) : undefined,
      consumerProfileUpdate: true,
      profilePreferenceId: undefined,
      panAvailable: false,
    } as any);
    return payloadDraft;
  };

  // Compute missing required fields like Send's missingFieldsFound flow
  const computeBillPayMissingRequiredFields = (): any[] => {
    const flatFields = billPayAllRequiredFieldsStore as any[] | null;
    const validationReqDraft = buildBillPayValidationDraft();
    return new RequiredFields().getMissingFieldsForBP(flatFields || [], validationReqDraft);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex(s => s.key === "previosBillerInfo");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleBillerSelected = (_biller: any) => {
    const targetIndex = steps.findIndex(s => s.key === "accountInformation");
    if (targetIndex !== -1) {
      try { recordStepTransition?.("accountInformation"); } catch { }
      setCurrentStep(targetIndex);
    }
  };

  return (
    <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={Platform.OS === "ios" ? 20 : 0}
      >
          {/* --- HEADER --- */}
          <View style={styles.header}>
            {/* Top Bar */}
            <View style={styles.topBar}>
              {(steps[currentStep].key !== "receiverReferenceNumber") ? (
                <TouchableOpacity style={styles.topBarButton} onPress={handleBack}>
                  <Image source={BackIcon} style={styles.IconStyle} />
                </TouchableOpacity>
              ) : (
                <Text></Text>
              )
              }
              <View style={styles.topBarRight}>
                <TouchableOpacity style={styles.topBarButton}>
                  <Image source={HelpIcon} style={styles.IconStyle} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.topBarButton}
                  onPress={handleClose}
                >
                  <Image
                    source={CloseIcon}
                    style={[styles.IconStyle, { marginLeft: 10 }]}
                  />
                </TouchableOpacity>
              </View>
            </View>

            {/* Progress Bar */}
            <View style={styles.progressRow}>
              {steps.map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.progressSegment,
                    { backgroundColor: i <= currentStep ? "#000" : "#ccc" },
                  ]}
                />
              ))}
            </View>
          </View>

          {/* --- STEP CONTENT --- */}
            <ScrollView
              contentContainerStyle={[styles.scrollContainer, { paddingBottom: 80 }]}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
            {steps[currentStep].key === "pin" ? (
              <AnyStepComponent
                onValidationChange={handleValidationChange}
                handleForgotPassword={handleForgotPassword}
                attemptsLeft={attemptsLeft}
                locked={locked}
                resetSignal={resetSignal}
              />
            ) : (steps[currentStep].key === "reviewAndEdit") || (steps[currentStep].key === "billPayFinalReview") ? (
              <AnyStepComponent onEditStep={handleEditStep} onContinue={handleNext} />
            ) : (steps[currentStep].key === "payAmount") ? (
              <AnyStepComponent onContinue={handleNext} onEditStep={handleEditStep} />
            ) : (
              <AnyStepComponent
                ref={phoneStepRef}
                onValidationChange={handleValidationChange}
                {...(steps[currentStep].key === "previosBillerInfo" ? { onSelectBiller: handleBillerSelected } : {})}
              />
            )}
          </ScrollView>
          {/* Modal for reference number result */}
          <Modal
            visible={showRefResultModal}
            transparent
            animationType="fade"
            onRequestClose={() => setShowRefResultModal(false)}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.modalContainer}>
                <Text style={styles.modalTitle}>Transaction Details</Text>
                <View style={styles.modalDivider} />
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.modalRowLabel}>Receiver Name</Text>
                  <Text style={styles.modalRowValue}>{refResult?.receiverName || '-'}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>Sender Name</Text>
                  <Text style={styles.modalRowValue}>{refResult?.senderName || '-'}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>Receive Amount</Text>
                  <Text style={styles.modalRowValue}>{String(refResult?.receiveAmount ?? '-')}</Text>
                  <Text style={[styles.modalRowLabel, { marginTop: 8 }]}>Reference Number</Text>
                  <Text style={styles.modalRowValue}>{String(refResult?.referenceNumber ?? '-')}</Text>
                </View>
                <TouchableOpacity style={[styles.button, { marginTop: 16 }]} onPress={() => setShowRefResultModal(false)}>
                  <Text style={styles.buttonText}>OK</Text>
                </TouchableOpacity>
              </View>
            </View>
          </Modal>
          {/* --- FIXED FOOTER --- */}
          { !["payAmount", "reviewAndEdit", "billPayFinalReview"].includes(steps[currentStep].key) ? (
          <View style={styles.footerContainer}>
            {(steps[currentStep].key !== "previosBillerInfo") && (
              <TouchableOpacity
                style={[
                  styles.button,
                  { backgroundColor: isStepValid ? "#000" : "#999",
                    marginTop: 14,
                   },
                ]}
                onPress={() => handleNext()}
                disabled={!isStepValid}
              >
                <Text style={styles.buttonText}>
                  {currentStep === steps.length - 1 ? "I’m finished" : "Continue"}
                </Text>
              </TouchableOpacity>
            )}
            {(steps[currentStep].key === "pin" ||
            steps[currentStep].key === "confirmPin") && (
              <TouchableOpacity onPress={steps === EXISTING_CUNSUMER_STEPS ? handleForgotPassword : handleSkip } style={{ marginTop: 8, alignItems: "center" }}>
                <Text style={styles.contniueAsGuest}>
                  {steps === EXISTING_CUNSUMER_STEPS ? t("CONTINUE_AS_GUEST") : 'Skip'}
                </Text>
              </TouchableOpacity>
            )}
            {(steps[currentStep].key === "receiverReferenceNumber") && (
              <TouchableOpacity onPress={handleSkip} style={{ marginTop: 8, alignItems: "center" }}>
                <Text style={{ color: "#2563EB", textDecorationLine: "underline" }}>Disclaimer</Text>
              </TouchableOpacity>
            )}
          
          </View>
          ): (<></>)}
      </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white
  },
  header: {
    paddingHorizontal: 24
  },
  content: {
    flex: 1,
    // width: "100%",
  },
  scrollContainer: {
    flexGrow: 1,
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: Colors.white
  },
  footerContainer: {
    // Ensures button stays fixed at bottom, but moves up with keyboard
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderColor: "#F2F2F2",
    paddingHorizontal: 24
  },
  button: {
    width: "100%",             // button always full width
    backgroundColor: "#000",
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: Platform.OS === "ios" ? 24 : 16,
  },
  contniueAsGuest: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '500',
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12
  },
  topBarButton: {
    padding: 1
  },
  topBarRight: {
    flexDirection: "row"
  },
  IconStyle: {
    height: 24,
    width: 24
  },
  iconText: {
    fontSize: 20,
    color: "#111"
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 6
  },
  progressSegment: {
    flex: 1,
    height: 6,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600"
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center"
  },
  modalContainer: {
    width: "88%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 12
  },
  modalRowLabel: {
    fontSize: 14,
    color: "#555"
  },
  modalRowValue: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111"
  },
  modalDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginTop: 8
  },
});